import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import InfoHint from '../common/InfoHint';

type AutopilotBetaBadgeProps = {
  compact?: boolean;
};

const AutopilotBetaBadge: React.FC<AutopilotBetaBadgeProps> = ({ compact = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? 0.4 : 0.55,
        flexWrap: 'nowrap',
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: compact ? 0.7 : 0.85,
          py: compact ? 0.18 : 0.24,
          borderRadius: '999px',
          border: `1px solid ${alpha(
            theme.palette.success.main,
            theme.palette.mode === 'dark' ? 0.34 : 0.2
          )}`,
          backgroundColor: alpha(
            theme.palette.success.main,
            theme.palette.mode === 'dark' ? 0.12 : 0.08
          ),
          color: theme.palette.mode === 'dark' ? '#9ae6b4' : '#166534',
          boxShadow:
            theme.palette.mode === 'dark'
              ? 'inset 0 1px 0 rgba(255,255,255,0.03)'
              : 'inset 0 1px 0 rgba(255,255,255,0.5)',
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: compact ? '0.62rem' : '0.68rem',
            fontWeight: 800,
            letterSpacing: '0.08em',
            lineHeight: 1,
            textTransform: 'uppercase',
          }}
        >
          {t('autopilot.beta.badge', { defaultValue: 'Beta' })}
        </Typography>
      </Box>
      <InfoHint
        ariaLabel={t('autopilot.beta.ariaLabel', {
          defaultValue: 'Autopilot beta information',
        })}
        title={t('autopilot.beta.title', { defaultValue: 'Autopilot beta' })}
        content={t('autopilot.beta.content', {
          defaultValue:
            'Autopilot is currently in beta testing. Selection logic, automation rules, and interface details are still being tuned. Use conservative limits, monitor managed bots regularly, and expect changes while the feature is being validated in production.',
        })}
        maxWidth={360}
        buttonSize={compact ? 20 : 22}
        iconSize={compact ? 13 : 14}
      />
    </Box>
  );
};

export default AutopilotBetaBadge;
